import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBranches, useIsBranchScoped, useScopedBranchLabel } from './branch-scope';

/**
 * The dashboard's page-level branch filter, held in the URL as `?branchId=`.
 *
 * URL rather than localStorage, deliberately. A sticky branch filter means an
 * operator opens the dashboard weeks later and reads materially under-reported
 * money with no visible cause — the address bar is a permanent, shareable,
 * back-button-correct explanation. (Reports keeps its filter in component state
 * because it sits inches from the table it filters; here the control is far
 * from most of what it affects, so the risk is qualitatively different.)
 *
 * "Is the page filtered" is NOT just "did we pick a branch": a branch-scoped
 * operator never touches the picker, sends no param, and still gets pinned
 * figures from the server. `isScoped` reads the same JWT scope the server
 * clamps on, so the two agree; the server's `appliedBranchId` echo is the
 * cross-check the caller renders in the filter bar.
 */
export function useDashboardBranch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: branches } = useBranches();
  const isScoped = useIsBranchScoped();
  const scopedLabel = useScopedBranchLabel();

  const raw = searchParams.get('branchId') ?? undefined;
  // Until the branch list lands, honour the param as-is rather than blanking
  // the page on first paint.
  const known = branches ? branches.some((b) => b.id === raw) : true;
  const branchId = raw && known ? raw : undefined;
  const staleParam = !!raw && !known;

  // A bookmarked filter for a branch that has since been deleted would
  // otherwise render empty widgets with no explanation. Drop it from the URL.
  useEffect(() => {
    if (!staleParam) return;
    const next = new URLSearchParams(searchParams);
    next.delete('branchId');
    setSearchParams(next, { replace: true });
  }, [staleParam, searchParams, setSearchParams]);

  function setBranchId(next: string | undefined) {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('branchId', next);
    else params.delete('branchId');
    setSearchParams(params, { replace: true });
  }

  const branch = branches?.find((b) => b.id === branchId);
  const filtered = branchId != null || isScoped;

  return {
    /** What to SEND to the API. Undefined for a scoped operator — the server pins them. */
    branchId,
    setBranchId,
    clear: () => setBranchId(undefined),
    isScoped,
    filtered,
    staleParam,
    branchLabel: branch ? `${branch.name} (${branch.code})` : (scopedLabel ?? null),
  };
}
